import React, { useState, useEffect, useRef } from 'react';
import { Download, CheckCircle, AlertCircle, Ticket, Mail } from 'lucide-react';
import QRCode from 'qrcode.react';

const API_BASE = '/api';

const BadgeSection = ({ profile, participantId, eventColor, eventSlug, onBadgeGenerate, t = () => '' }) => {
  const [savedBadge, setSavedBadge]   = useState(null);
  const [fetching, setFetching]       = useState(true);
  const [autoGenerating, setAutoGenerating] = useState(false);
  const [error, setError]             = useState(null);
  const qrContainerRef                = useRef(null);

  const stripeColor = eventColor || '#2563EB';

  const recordId = profile?.record_id || participantId;
  const qrPayload = (profile && recordId && eventSlug)
    ? `https://matchmaking.mobivent.fr/event/${eventSlug}/participants-networking/${recordId}`
    : null;

  // ── Charger les badges existants, puis auto-générer si absent ───────────
  useEffect(() => {
    const rid = profile?.record_id || participantId;
    if (!rid) { setFetching(false); return; }
    let cancelled = false;
    (async () => {
      try {
        // 1. Vérifier si badge déjà existant
        const res  = await fetch(`${API_BASE}/badges/participant/${rid}`);
        const json = await res.json();
        const exists = json.success &&
          json.data?.qr_code_url &&
          json.data.qr_code_url.includes('/uploads/badges/');

        if (!cancelled && exists) {
          setSavedBadge(json.data);
          setFetching(false);
          return;
        }

        // 2. Pas de badge → générer silencieusement
        if (!cancelled) {
          setFetching(false);
          setAutoGenerating(true);
          try {
            await fetch(`/api/generate.php?record_id=${encodeURIComponent(rid)}&silent=1`);
            if (!cancelled) setSavedBadge({ generated_at: new Date().toISOString() });
          } catch (e) {
            console.warn('Auto-generation failed', e);
          } finally {
            if (!cancelled) setAutoGenerating(false);
          }
        }
      } catch (e) {
        console.warn('Badge fetch failed', e);
        if (!cancelled) setFetching(false);
      }
    })();
    return () => { cancelled = true; };
  }, [profile?.record_id, participantId]);

  // ── Télécharger le badge PDF ──────────────────────────────────────────────
  // Si le fichier existe déjà sur le serveur → téléchargement direct
  // Sinon → génération à la volée via generate.php
  const downloadBadge = () => {
    const rid      = profile?.record_id || participantId;
    const filename = `badge-${profile?.firstname || ''}-${profile?.lastname || ''}.pdf`;
    const link     = document.createElement('a');
    link.download  = filename;
    link.href      = savedBadge?.qr_code_url
      ? savedBadge.qr_code_url
      : `/api/generate.php?record_id=${encodeURIComponent(rid)}`;
    link.click();
  };

  // ── Ouvrir Apple Wallet ───────────────────────────────────────────────────
  const openAppleWallet = () => {
    const rid = profile?.record_id || participantId;
    if (!rid) return;
    window.location.href = `/api/generate_wallet.php?record_id=${encodeURIComponent(rid)}`;
  };

  // ── ÉTATS D'ATTENTE ───────────────────────────────────────────────────────
  if (fetching) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" />
        <span className="ml-3 text-gray-400 text-sm">{t('badge.loading') || 'Chargement…'}</span>
      </div>
    );
  }

  if (!profile || !qrPayload) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
        {t('badge.loading') || 'Chargement du profil…'}
      </div>
    );
  }

  // ── RENDER PRINCIPAL ──────────────────────────────────────────────────────
  const hasBadge = !!savedBadge;

  return (
    <div className="space-y-5">

      {/* Erreur */}
      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* ── Carte principale ── */}
      <div className="bg-white border rounded-xl shadow-sm overflow-hidden">

        {/* Stripe couleur événement */}
        <div className="h-2 w-full" style={{ backgroundColor: stripeColor }} />

        {/* En-tête */}
        <div className="px-6 py-4 bg-gray-50 border-b flex items-center gap-3">
          <Ticket className="w-5 h-5 flex-shrink-0" style={{ color: stripeColor }} />
          <div className="min-w-0">
            <p className="font-semibold text-gray-800 truncate">
              {profile.firstname} {profile.lastname}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {profile.company_name_from_companies || profile.company || ''}
              {profile.fonction ? ` · ${profile.fonction}` : ''}
            </p>
          </div>
          {hasBadge && (
            <CheckCircle className="w-5 h-5 text-green-500 ml-auto flex-shrink-0" />
          )}
        </div>

        {/* QR code */}
        <div className="flex flex-col items-center py-8 gap-3">
          <div
            ref={qrContainerRef}
            className="p-3 bg-white rounded-xl shadow-sm"
            style={{ border: `2px solid ${stripeColor}20` }}
          >
            <QRCode
              value={qrPayload}
              size={200}
              level="H"
              includeMargin={false}
              renderAs="canvas"
              fgColor={stripeColor}
            />
          </div>

          {autoGenerating ? (
            <span className="text-xs text-blue-500 flex items-center gap-1.5">
              <div className="animate-spin h-3 w-3 border-2 border-blue-500 border-t-transparent rounded-full" />
              {t('badge.creating') || 'Création en cours…'}
            </span>
          ) : hasBadge && savedBadge.generated_at ? (
            <span className="text-xs text-green-600 font-medium flex items-center gap-1">
              <CheckCircle className="w-3.5 h-3.5" />
              {t('badge.saved_on') || 'Sauvegardé le'} {new Date(savedBadge.generated_at).toLocaleDateString('fr-FR')}
            </span>
          ) : null}
        </div>

        {/* Boutons */}
        <div className="px-6 pb-6 flex flex-col gap-3">

          {/* Télécharger le badge PDF */}
          <button
            onClick={downloadBadge}
            disabled={autoGenerating}
            className="w-full flex items-center justify-center gap-2 px-5 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            {t('badge.download_badge') || 'Télécharger mon badge'}
          </button>

          {/* Apple Wallet */}
          {(profile?.record_id || participantId) && (
            <button
              onClick={openAppleWallet}
              className="w-full flex items-center justify-center"
            >
              <img
                src="/add-to-wallet.svg"
                alt={t('badge.add_apple_wallet') || 'Add to Apple Wallet'}
                className="h-12"
                onError={e => {
                  // Fallback texte si le SVG ne charge pas
                  e.target.style.display = 'none';
                  e.target.parentNode.classList.add(
                    'gap-2','px-5','py-2.5','bg-black','text-white',
                    'rounded-xl','text-sm','font-medium'
                  );
                  e.target.parentNode.insertAdjacentHTML(
                    'beforeend',
                    '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>'
                    + '<span>' + (document.documentElement.lang === 'de' ? 'Zu Apple Wallet' : document.documentElement.lang === 'fr' ? 'Ajouter à Apple Wallet' : 'Add to Apple Wallet') + '</span>'
                  );
                }}
              />
            </button>
          )}

          {/* Envoyer par email — à brancher */}
          <button
            disabled
            className="w-full flex items-center justify-center gap-2 px-5 py-2 border border-gray-200 text-gray-400 rounded-xl text-sm cursor-not-allowed"
          >
            <Mail className="w-4 h-4" />
            {t('badge.send_email') || 'Envoyer par email'}
            <span className="ml-1 text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full">{t('badge.coming_soon') || 'Bientôt'}</span>
          </button>

        </div>
      </div>

    </div>
  );
};

export default BadgeSection;
